import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Link } from '@reach/router'
import { isCampaignProperty, hasActiveCampaign } from 'utils/checkCampaign'
import {
  Address,
  Image,
  AprraisedValue,
  Feature,
  Discount,
  Tag,
  Button,
  SaleValueText,
  Tooltip,
} from 'components'
import noop from 'lib/noop'
import CONSTANTS from 'utils/systemsConstants'
import getPropertyCategoryImage from 'utils/getPropertyCategoryImage'
import styles from './PropertyCard.module.scss'

const PropertyCard = ({ immobile, onClick, navigateRouter }) => {
  const width = window.innerWidth
  const {
    name,
    photo,
    longPhoto,
    address,
    saleCategory,
    city,
    state,
    description,
    features,
    appraisedValue,
    appraisedLabel,
    desagio,
    saleValue,
    saleLabel,
    tags,
    soldStatus,
    category,
    campaignInfo,
  } = immobile

  const {
    values: { campaignTotalValue },
  } = campaignInfo

  const hasPromotion = Boolean(
    campaignTotalValue && isCampaignProperty(tags) && hasActiveCampaign()
  )

  const isJudicialSale = saleCategory === 'venda_judicial'
  const isFriendlySale = saleCategory === 'venda_amigavel'

  const nameCampaignValue = () => {
    if (tags.includes(process.env.REACT_APP_CAMPAIGN)) {
      return process.env.REACT_APP_CAMPAIGN
    }
    return process.env.REACT_APP_SECOND_CAMPAIGN
  }

  const isSold = soldStatus === 'vendido'
  const { CONCORRENCIA } = CONSTANTS

  return (
    <article className={styles.card}>
      <figure className={classNames(styles.image, { [styles.sold]: isSold })}>
        {/* <FavoriteButton
          value={favoriteButton.value}
          onClick={favoriteButton.onClick}
        /> */}
        <Link to={navigateRouter} title={`Link para o imóvel ${name}`}>
          <Image
            alt={`Foto do imóvel ${name}`}
            sourceMobile={longPhoto}
            source={photo}
            sourceLarge={photo}
            noPhoto={getPropertyCategoryImage(category)}
            noPhotoLarge={getPropertyCategoryImage(`${category}Large`)}
            onClick={onClick}
          />
        </Link>
        {isSold && (
          <div>
            <span>VENDIDO</span>
          </div>
        )}
      </figure>

      <div className={styles.information}>
        <div className={styles.tags}>
          {isJudicialSale && <Tag key="venda_judicial">Venda Judicial</Tag>}
          {isFriendlySale && <Tag key="venda_amigavel">Venda Amigável</Tag>}
          {tags.map(tag => (
            <Tag campaign={tag === CONCORRENCIA} key={tag}>
              {tag}
            </Tag>
          ))}
        </div>

        <header>
          <Link to={navigateRouter} className={styles.link}>
            <Tooltip className={styles.propertyText}>{name}</Tooltip>
          </Link>
        </header>

        <p className={styles.resumeAddress}>{`${city}/${state}`}</p>

        <Address address={address} />

        <Tooltip ignoreTooltipTreatment className={styles.ellipsis}>
          {description}
        </Tooltip>

        <div className={styles.features}>
          {features.map(({ id, feature, amount, icon }) => (
            <Feature
              key={id}
              icon={icon}
              number={amount}
              feature={feature}
              type="row"
              propertyCard
            />
          ))}
        </div>
      </div>
      <div className={styles.values}>
        {appraisedValue > saleValue ? (
          <span className={classNames(styles.prices)}>
            <AprraisedValue
              value={appraisedValue}
              strike={appraisedValue !== saleValue}
              label={appraisedLabel}
              isCampaign={hasPromotion}
            />

            {hasPromotion ? (
              <>
                {' '}
                <SaleValueText
                  saleLabel={saleLabel || 'Valor de venda'}
                  value={saleValue}
                  propertyCard
                  isCampaign={hasPromotion}
                />
                <Discount percent={desagio} withIcon={width > 1024} />
                <SaleValueText
                  saleLabel={`Valor ${nameCampaignValue()}`}
                  value={campaignTotalValue}
                  propertyCard
                  className={styles.promotionPrice}
                />
              </>
            ) : (
              <>
                {' '}
                <Discount percent={desagio} withIcon={width > 1024} />
                <SaleValueText
                  saleLabel={saleLabel || 'Valor de venda'}
                  value={saleValue}
                  propertyCard
                  isCampaign={hasPromotion}
                />
              </>
            )}
          </span>
        ) : (
          <span>
            <SaleValueText
              saleLabel={saleLabel || 'Valor de venda'}
              value={saleValue}
              propertyCard
              isCampaign={hasPromotion}
            />
            {!!hasPromotion && (
              <SaleValueText
                saleLabel={`Valor ${nameCampaignValue()}`}
                value={campaignTotalValue}
                propertyCard
                className={styles.promotionPrice}
              />
            )}
          </span>
        )}
        <Link to={navigateRouter} className={styles.buttonOpportunity}>
          <Button
            onClick={() => {}}
            className={styles.button}
            fontSize="xsmall"
            disabled={isSold}
          >
            {isSold ? 'VENDIDO' : 'VER OPORTUNIDADE'}
          </Button>
        </Link>
      </div>
      <Link to={navigateRouter} className={styles.mobileNavigate} />
    </article>
  )
}

PropertyCard.propTypes = {
  immobile: PropTypes.shape({
    name: PropTypes.string,
    photo: PropTypes.string,
    longPhoto: PropTypes.string,
    saleCategory: PropTypes.string,
    address: PropTypes.string,
    description: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.object),
    appraisedValue: PropTypes.number,
    desagio: PropTypes.number,
    saleValue: PropTypes.number,
    tags: PropTypes.arrayOf(PropTypes.string),
    soldStatus: PropTypes.string,
    saleLabel: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    appraisedLabel: PropTypes.string,
    category: PropTypes.string,
    campaignInfo: PropTypes.shape().isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  navigateRouter: PropTypes.string.isRequired,
  favoriteButton: PropTypes.shape({
    onClick: PropTypes.func,
    value: PropTypes.bool,
  }),
}

PropertyCard.defaultProps = {
  favoriteButton: {
    onClick: noop,
    value: false,
  },
}

export default React.memo(PropertyCard)
